import { eSendChannel, CHANNELS, ChannelDescriptor } from '../../../Models/DataSources/SmartSend';
import { EMAIL_SENT_STATUSES, WaVariableMap } from '../../../Models/DataSources/SmartSendWhatsApp';
import { ChannelStateItem } from '../../../Models/DataSources/SmartSendChannels';

// SmartSend part B — the WhatsApp derived state. CONTRACT §6 names, verbatim.
//
// Every predicate that decides whether a control exists lives HERE, once. The lesson the
// contract records five times over (Z-01, AA-01, Q1-2) is that a rule copied into two
// components drifts, and the drift is only visible as a screen that contradicts itself.
//
// These take `state` (the whole store) rather than being `createSelector`s: the screen reads
// three slices (core.userRoles, smartSend, smartSendWa) and memoisation buys nothing here —
// every input is a scalar or an already-memoised slice object.

export interface WaRootState {
    core?: { userRoles?: any };
    smartSendWa?: any;
    smartSend?: any;
    dataSources?: any;
}

const wa = (s: WaRootState) => s.smartSendWa ?? {};

// ══ 09-channel-units — ON THE MAPPING SCREEN THE MODE IS WHICH UNITS THE PAGE CARRIES ═══════════
// Every channel is its own unit (its own container, its own mapping row — 06-docs §1.1), and the
// mapping screen names its units in the URL: `?channel=3` ⇒ the page IS the WhatsApp unit,
// `?wa={id}` ⇒ the e-mail page carries the WhatsApp unit `{id}` as well, neither ⇒ e-mail alone.
// `SmartSendScreen` stores that as `unitMode`, and where it is set it is the WHOLE answer: no leg
// bit, no `dbo.CampaignChannel` row and no leftover intent can make a page carry a unit its URL
// does not name. `null` (the picker) keeps the intent derivations below exactly as they were.
type UnitMode = 'email' | 'both' | 'whatsapp';
const unitMode = (s: WaRootState): UnitMode | null => wa(s).unitMode ?? null;

// § THE PERMISSION GATE. `AllowWhatsAppToAgent` is the SAME condition that guards the WhatsApp
// routes in App.js:311-376. Without it the "manage WhatsApp templates" link inside the blocked
// card points at a route that is not registered for this user — so the whole add-on is not
// rendered and NO WhatsApp/* call is made, exactly like Enabled=false (contract §6, invariant 12).
export const selectWaAllowed = (s: WaRootState): boolean => !!s.core?.userRoles?.AllowWhatsAppToAgent;

export const selectWaEnabled = (s: WaRootState): boolean => !!wa(s).waAvailability?.Enabled;

// ══ 67 (5) · 70 (4.1) — THE SERVER CHANNEL ARRAY IS THE AUTHORITY ═══════════════════════════
//
// 🔴 WHAT CHANGED AND WHY. Until this wave "which channels are on" was derived from
// `waMapping.HasLeg` plus `suppressEmail` - a fact stored ON THE WHATSAPP LEG answering a
// question ABOUT THE E-MAIL. A leg survives a soft delete (67 (3.5)), so "the leg is here" and
// "the channel is on" stopped being the same sentence (67 (3.3.0)), and the screen could say
// "off" while the send path still said "on".
//
// ⚠️ TRI-STATE ON PURPOSE. `null` is "not read yet", which is NOT the same answer as `false`.
// Collapsing them is how a loading screen starts asserting things about a campaign it has not
// read. Every consumer below states what it does with `null`, and none of them guess.
const serverChannels = (s: WaRootState): ChannelStateItem[] | null => (wa(s).channels ?? null);

// The array belongs to THIS campaign and arrived on a 200. Without the id check a stale array
// from the previous campaign answers for the current one during navigation - and "the WhatsApp
// channel is on" is exactly the answer that must never be carried over.
export const selectChannelsReady = (s: WaRootState): boolean =>
    wa(s).channelsStatus === 'succeeded'
    && wa(s).channelsCampaignId != null
    && wa(s).channelsCampaignId === wa(s).campaignId;

// true | false | null("not read yet").
// ⚠️ AN ELEMENT THAT IS ABSENT FROM A LOADED ARRAY IS AN ANSWER, NOT A GAP: the server omits
// the WhatsApp element entirely when the add-on is not installed on that server (invariant 8,
// "off ⇒ byte-identical"). So absent ⇒ off, except for e-mail, which is on when no row exists
// at all - that is 67 (3.1)'s backward-compatible rule and the server already applied it.
export const selectServerChannelActive = (s: WaRootState, key: ChannelKey): boolean | null => {
    if (!selectChannelsReady(s)) return null;
    const arr = serverChannels(s);
    if (!arr) return null;
    const it = arr.find((c) => c.Key === key);
    return it ? !!it.IsActive : key === 'email';
};

export const selectServerChannelItem = (s: WaRootState, key: ChannelKey): ChannelStateItem | null =>
    serverChannels(s)?.find((c) => c.Key === key) ?? null;

// 09-channel-units — on the mapping screen `unitMode` is the whole answer: the page has WhatsApp
// exactly when its URL names a WhatsApp unit. The rest is the PICKER's answer (`unitMode` null
// there, and the unit context cleared on its arrival): the operator's tick, `withWhatsApp`. The
// channel array and `waMapping.HasLeg` arms are read only if something left them in the store —
// nothing fetches the array any more, and no leg is ever created again (409 WA_LEG_RETIRED).
export const selectWithWhatsApp = (s: WaRootState): boolean => {
    const u = unitMode(s);
    if (u) return u !== 'email';
    const server = selectServerChannelActive(s, 'whatsapp');
    if (server !== null) return server || !!wa(s).withWhatsApp;
    return !!(wa(s).waMapping?.HasLeg || wa(s).withWhatsApp);
};

// ── THE CHANNEL STATE ─────────────────────────────────────────────────────────────────────────
// 🔴 09-channel-units — THREE STATES, AND THEY ARE THE PAGE'S UNITS (the URL, via `unitMode`):
//     e-mail page            ⇒ e-mail only
//     "both" page (?wa={w})  ⇒ both — the e-mail campaign plus the WhatsApp unit w
//     unit page (?channel=3) ⇒ WhatsApp only — the page IS the unit
// This drives ROUTING in `SendSummaryDialog` (`selectEmailOn` is its complement). No leg and no
// `SuppressEmail` bit decides it any more: every "both" send names its unit (`waCampaignId`), and
// no leg is ever created again (409 WA_LEG_RETIRED).
//
// ⚠️ The arms below `unitMode` are the pre-units derivation, reached only where `unitMode` is null
// (the picker, which never routes a send). The SERVER refuses on its own either way: PUT Send
// reads dbo.CampaignChannel's e-mail row itself, and a WhatsApp unit is only ever sent by its own id.
export const selectWaOnly = (s: WaRootState): boolean => {
    const u = unitMode(s);
    if (u) return u === 'whatsapp' && selectWaActive(s);
    const waOn = selectServerChannelActive(s, 'whatsapp');
    const emailOn = selectServerChannelActive(s, 'email');
    if (waOn === null || emailOn === null) {
        return selectWaActive(s) && !!wa(s).waMapping?.HasLeg && !!wa(s).suppressEmail;
    }
    return waOn && !emailOn;
};

// ── C-29 · "WHATSAPP ONLY" AS THE OPERATOR'S INTENT ─────────────────────────────────────────────
// 09-channel-units: on the mapping screen this and `selectWaOnly` give the SAME answer — the page
// IS the WhatsApp unit (`unitMode` 'whatsapp', from `?channel=3`) — because the unit exists before
// the page opens (the picker's Continue creates it). On the PICKER (`unitMode` null) it is the
// operator's e-mail switch, `suppressEmail`, which decides whether Continue creates the WhatsApp
// unit alone or beside the e-mail campaign. No leg is involved on either screen any more.
export const selectWaOnlyIntent = (s: WaRootState): boolean => {
    const u = unitMode(s);
    if (u) return u === 'whatsapp' && selectWaActive(s);
    return selectWaActive(s) && !!wa(s).suppressEmail;
};

export const selectEmailOnIntent = (s: WaRootState): boolean => !selectWaOnlyIntent(s);

// 🔴 THE SEND GATE ON THE WHATSAPP UNIT'S OWN PAGE: until the unit's own GET (channel 3) has
// answered — a unit's GetMapping reports HasLeg = 1 — "שלח לכולם" is refused and ActionBar says
// why. 09-channel-units: that page sends through `PUT WhatsAppOnly/Send/{unit}` (chosen by
// `?channel=3`), so this gate is about an unread unit, never about a mail going out.
export const selectWaSetupIncomplete = (s: WaRootState): boolean =>
    selectWaOnlyIntent(s) && !wa(s).waMapping?.HasLeg;

// ══ THE CHANNEL SET ══════════════════════════════════════════════════════════════════════════
//
// 🔴 A BOOLEAN CAN ONLY EVER DESCRIBE TWO CHANNELS, AND WE ARE GETTING A THIRD.
// `SuppressEmail` is one bit and it stays one bit — it is the STORAGE, and it expresses three
// states only because there happen to be two channels. Three channels are seven combinations,
// and no bit describes those. What changes is what the CLIENT holds: a set, asked "who is on",
// instead of a flag asked "is it the other one".
//
// ⚠️ THREE RULES FROM HERE ON, and they are why `sms` is in the type today even though it is
// false today:
//   · no new field assumes "two";
//   · no `else` that means "then it is the other channel";
//   · adding SMS is a branch in the row builders and nothing else — `CHANNEL_ORDER` already
//     carries it, DERIVED from `CHANNELS` — not hunting for every `waOnly ? … : …` in the tree.
//
// 🟢 AND THIS IS NOT A BEHAVIOUR CHANGE. The same three states, in a representation that can
// hold a fourth. Every predicate below is derived from the same two facts it always was.
export type ChannelKey = 'email' | 'whatsapp' | 'sms';

export interface ActiveChannels { email: boolean; whatsapp: boolean; sms: boolean; }

// 🔴 ONE REGISTRY — AND THE BRIDGE IS BUILT ON THIS SIDE BECAUSE THE REGISTRY IS FROZEN.
// `CHANNELS` (`Models/DataSources/SmartSend.ts:24`) is the single source of truth and already
// carries `identityFlag`, `clientIdField`, `resolvedCountField` and `duplicateFlag` for all
// three channels, SMS included. That file is BYTE-IDENTICAL under contract §1, so no field is
// added to the descriptor: the key is MAPPED onto it from here.
// ⚠️ `Record<eSendChannel, …>` is not decoration. Add a member to the enum and this map stops
// COMPILING — which is the only moment anyone would otherwise notice that a channel exists with
// no key, no order and no row.
const KEY_BY_CHANNEL: Record<eSendChannel, ChannelKey> = {
    [eSendChannel.EMAIL]: 'email',
    [eSendChannel.SMS]: 'sms',
    [eSendChannel.WHATSAPP]: 'whatsapp',
};

// The order channels are presented AND iterated in — DERIVED, so a loop and a layout cannot
// disagree about which comes first.
// 🔴 AND THEY ALREADY DID DISAGREE. This was hand-written as `['email', 'whatsapp', 'sms']`
// while `ChannelSelector` renders `CHANNELS` in ITS order — e-mail, SMS, WhatsApp. The second
// representation was not merely duplicated; it had DRIFTED, and the comment above it claimed
// the very property it no longer had.
// 🟢 NOTHING MOVES ON SCREEN TODAY: `sms` is never active, so `activeChannelKeys` returns the
// same list either way. What changes is where the SMS row lands the day it is switched on —
// beside its own card instead of after WhatsApp.
export const CHANNEL_ORDER: ChannelKey[] = CHANNELS.map((c) => KEY_BY_CHANNEL[c.channel]);

// Key ⇒ descriptor, so a predicate can ask a channel for its FIELD NAMES instead of hardcoding
// `HasEmailIdentity` / `HasCellIdentity` per channel. Note WhatsApp and SMS legitimately share
// `HasCellIdentity`: identity is a property of the ADDRESS, not of the channel.
const CHANNELS_BY_KEY: Record<ChannelKey, ChannelDescriptor> = CHANNELS.reduce(
    (acc, c) => { acc[KEY_BY_CHANNEL[c.channel]] = c; return acc; },
    {} as Record<ChannelKey, ChannelDescriptor>,
);

// ⚠️ A PLAIN FUNCTION, NOT A SELECTOR OF THE STORE. A selector returning a fresh object
// re-renders its component on EVERY dispatch in the app — the trap this package already
// documents for `selectSourceCanSend`. Components select scalars and build the set here.
// 🔴 AND IT TAKES A PARTIAL, NOT TWO POSITIONAL BOOLEANS. `channelsFrom(a, b)` encoded "there
// are exactly two channels, and you know their order" into every call site — the shape 48 line 2
// forbids. A caller that NAMES its channel cannot transpose them, and the day SMS arrives no
// call site has to grow a third argument in the right position.
// ⚠️ `!!p[k]` and not a spread over an all-off base: a spread lets an explicit `undefined`
// overwrite the `false` underneath it, which is the one way this could answer `undefined` for a
// field whose entire job is to be a boolean.
export const channelsFrom = (p: Partial<ActiveChannels>): ActiveChannels =>
    CHANNEL_ORDER.reduce((acc, k) => { acc[k] = !!p[k]; return acc; }, {} as ActiveChannels);

export const activeChannelKeys = (ch: ActiveChannels): ChannelKey[] =>
    CHANNEL_ORDER.filter((k) => ch[k]);

// 🔴 WHICH ENDPOINT SENDS THIS CAMPAIGN — derived from WHO IS ON.
//
//   `email-pipeline`  `PUT Send/{c}` — carries the page's WhatsApp UNIT with it on a "both" page
//                     (`?waCampaignId={w}`) and attaches its outcome to its own answer.
//   `whatsapp-only`   the WhatsApp unit's own page. `SendSummaryDialog` sends it through
//                     `PUT WhatsAppOnly/Send/{w}` (chosen by `?channel=3`, checked first); the
//                     leg route `PUT WhatsApp/Send` behind this arm is legacy (09-channel-units).
//   `unsupported`     a set §5 has no route for: no channel at all, or a live channel no
//                     pipeline carries — "e-mail + SMS" the day SMS exists.
//
// ⚠️ THE THIRD ARM IS THE POINT. The old shape was `waOnly ? A : B`, which cannot represent
// "there is no route for this" — it would have sent a future SMS+WhatsApp campaign down the
// e-mail pipeline and mailed everyone. A total function that NAMES the gap cannot do that.
export type SendRoute = 'email-pipeline' | 'whatsapp-only' | 'unsupported';

// 🔴 WHAT THE E-MAIL PIPELINE CAN CARRY BESIDE THE MAIL — a LIST, not a count.
// The e-mail is not a member: it IS the pipeline. `whatsapp` is here because §5's
// `PUT Send/{id}` runs the leg and attaches its outcome; the day SMS is wired, wiring it is
// adding one string HERE, and until that day an SMS leg is named as uncarried rather than
// silently posted down a pipe that would mail everyone and drop it.
const PIPELINE_CARRIES: ChannelKey[] = ['whatsapp'];

export const sendRouteFor = (ch: ActiveChannels): SendRoute => {
    const active = activeChannelKeys(ch);
    const rest = active.filter((k) => k !== 'email');
    // 🔴 THIS ARM IS THE WHOLE CHANGE, AND IT FIRES **EVEN WITH THE MAIL ON**.
    // The previous body opened with `if (ch.email) return 'email-pipeline'`, so {e-mail, SMS}
    // answered "e-mail pipeline" — a route that sends the mail to everyone and carries no SMS
    // at all, with nothing anywhere saying the SMS was dropped. "Any set containing the e-mail"
    // was the wrong rule: what decides is whether every OTHER live channel can ride along.
    if (rest.some((k) => PIPELINE_CARRIES.indexOf(k) < 0)) return 'unsupported';
    if (active.indexOf('email') > -1) return 'email-pipeline';
    // No mail, and everything live is carried by the mail pipeline that is not running. Exactly
    // one such channel has a route of its own today.
    if (rest.length === 1 && rest[0] === 'whatsapp') return 'whatsapp-only';
    return 'unsupported';
};

// 🔴 THE E-MAIL CHANNEL, ASKED DIRECTLY RATHER THAN AS A COMPLEMENT (a SET has no "the other
// one"). 09-channel-units: on the mapping screen it is `unitMode` — the e-mail unit is in the
// send on the e-mail and "both" pages, not on the WhatsApp unit's own page. The array arm is the
// pre-units derivation for the picker (`unitMode` null). "Both channels off" is unrepresentable:
// every page carries at least one unit by construction of its URL.
export const selectEmailOn = (s: WaRootState): boolean => {
    const u = unitMode(s);
    if (u) return u !== 'whatsapp';
    const emailOn = selectServerChannelActive(s, 'email');
    return emailOn === null ? !selectWaOnly(s) : emailOn;
};

// §6: "לשוניות רק כששני הערוצים דלוקים; אחרת מסך אחד." One channel ⇒ no tab strip at all,
// which is also what the e-mail-only screen has always looked like.
// ⚠️ INTENT, not state (C-29): the tab strip is a display, and it has to follow the switch the
// operator just moved — not the leg that does not exist yet.
export const selectShowChannelTabs = (s: WaRootState): boolean =>
    selectWaActive(s) && selectEmailOnIntent(s);

// 🔴 The one condition every WhatsApp component renders behind. 14-W3 §2, corrected by
// v1.18 U6-M1: it is `AllowWhatsAppToAgent ∧ Enabled ∧ withWhatsApp` — and deliberately
// WITHOUT `HasLeg`: the WhatsApp tab must render (from the URL seed) before the unit's own GET
// has answered. (`selectWithWhatsApp` answers from the page's units — see there.)
export const selectWaActive = (s: WaRootState): boolean =>
    selectWaAllowed(s) && selectWaEnabled(s) && selectWithWhatsApp(s);

// ── the six leg states of §6, as one exhaustive discriminator ────────────────────────────────
// 0  noLeg           ¬HasLeg (tab rendered from URL/slice) — on a units page: the unit's own
//                    GET has not answered yet (a unit's GetMapping reports HasLeg = 1)
// 1  noHeader        HasLeg ∧ ¬HasHeader ∧ ¬SentHeaderDeleted
// 1b chargedNoHeader state 1 ∧ LastErrorCharged — LOCKED until the owner refunds
// 2  locked          HasLeg ∧ HasHeader ∧ WaCampaignStatus ≠ 1
// 3  waiting         HasLeg ∧ HasHeader ∧ Status = 1 ∧ EmailCampaignStatus ∈ {2,4}
// 4  normal          HasLeg ∧ HasHeader ∧ Status = 1 ∧ EmailCampaignStatus ∉ {2,4}
// 5  headerDeleted   HasLeg ∧ ¬HasHeader ∧ SentHeaderDeleted — read-only in full
//
// 🔴 EVERY branch tests `HasHeader` FIRST and never `Status ≠ 1` alone (v1.10 Z-01): without a
// live header all the header counters are NULL, and `null !== 1` used to lock the screen with
// "already sent" over a campaign that had never sent — with no way out, not even e-mail.
export type WaLegState = 'noLeg' | 'noHeader' | 'chargedNoHeader' | 'locked' | 'waiting' | 'normal' | 'headerDeleted';

export const selectWaLegState = (s: WaRootState): WaLegState => {
    const m = wa(s).waMapping;
    if (!m || !m.HasLeg) return 'noLeg';
    if (!m.HasHeader) {
        // Order matters: state (5) is tested BEFORE state (1), otherwise a campaign whose
        // header was deleted after sending falls through to "אשר תבנית" and offers a re-send.
        if (m.SentHeaderDeleted) return 'headerDeleted';
        return m.LastErrorCharged ? 'chargedNoHeader' : 'noHeader';
    }
    if (m.WaCampaignStatus !== 1) return 'locked';
    return EMAIL_SENT_STATUSES.indexOf(m.EmailCampaignStatus ?? -1) > -1 ? 'waiting' : 'normal';
};

// State (5). Full read-only: no "אשר תבנית", no "הסר וואטסאפ", no "נסה שוב", no map editing.
export const selectWaHeaderDeletedAfterSend = (s: WaRootState): boolean =>
    !!(wa(s).waMapping?.HasLeg && wa(s).waMapping?.SentHeaderDeleted);

// 🔴 State (1b) — invariant 26, the MONEY lock. Defined BY EFFECT, not by a list of controls:
// while it is on, no user action in the WhatsApp tab may produce a write. `saveWaMapping` and
// `markWaEdited` are the single choke point (see useWaAutosave), because in a header-less state
// THE FIRST EDIT IS THE FIRST POST — one cell change would run EnsureHeader, overwrite
// `leg.WhatsAppCampaignID` and erase the charge flag permanently, returning the screen to the
// clean state (1) and inviting a second charge of the whole audience with no warning.
// It clears by itself the moment the owner refunds the same @Guid (12 §4-6c).
export const selectWaLocked1b = (s: WaRootState): boolean => selectWaLegState(s) === 'chargedNoHeader';

// Which variable indexes still need a value. A binding whose SourceHeader is no longer in the
// locked version arrives with ColumnInVersion=false and its columnId is already dropped by the
// slice, so it counts as unmapped here — that is deliberate (contract §6, v1.5).
export const selectWaUnmapped = (s: WaRootState): number[] => {
    const st = wa(s);
    const map: WaVariableMap = st.waVariableMap ?? {};
    const n: number = st.waPlaceholderCount ?? 0;
    const out: number[] = [];
    for (let i = 1; i <= n; i += 1) {
        const b = map[i];
        const has = !!b && ((b.columnId != null && b.columnId > 0) || (b.staticValue != null && b.staticValue !== ''));
        if (!has) out.push(i);
    }
    return out;
};

export const selectWaMappedCount = (s: WaRootState): number =>
    (wa(s).waPlaceholderCount ?? 0) - selectWaUnmapped(s).length;

// 🔴 `HasHeader` — the SERVER field, never `WhatsAppCampaignID != null` (v1.10 Z-01). In state
// (1) it is false, so Send is disabled, and the way out is the WhatsApp tab's "אשר תבנית" (the button
// labelled `mapping.confirmTemplate` — a button, not a hint beside Send), or "הסר" on the tab strip.
// (QA BUG-13-r1: this used to call `mapping.confirmTemplate` the Send hint and name a "הסר וואטסאפ"
// that no WhatsApp tab renders since the units.)
export const selectWaCanSend = (s: WaRootState): boolean => {
    const m = wa(s).waMapping;
    return !!(m && m.HasLeg && m.HasHeader) && selectWaUnmapped(s).length === 0;
};

// § THE LATE-SEND PATH (invariant 22). Server state ONLY — never `waSendOutcome` in memory,
// which disappears on refresh, and never a render gated on `WaCampaignStatus ≠ 1`, which is the
// exact contradiction that made v1.7's retry button unreachable (X-01).
export const selectWaRetryEligible = (s: WaRootState): boolean => {
    const m = wa(s).waMapping;
    if (!m) return false;
    // 09-channel-units — a WhatsApp UNIT carries no e-mail status: it is its own campaign, and its
    // retry is its own send again (`PUT WhatsAppOnly/Send/{unit}`). Offered on the "both" page
    // after that send FAILED in this session; a WhatsApp-only page keeps its ordinary send button,
    // and a unit that went out or is going out (Status ≠ 1) has nothing to retry.
    // Not on a charge that was never refunded: the server refuses that retry every time
    // (WA_ALREADY_SENT), and the receipt line says "request a refund" instead.
    const u = unitMode(s);
    if (u) {
        return u === 'both' && !!m.HasLeg && !!m.HasHeader && m.WaCampaignStatus === 1
            && !m.LastErrorCharged && wa(s).waSendOutcome?.State === 'Failed';
    }
    return !!m.HasLeg
        && m.WhatsAppCampaignID != null
        && m.WaCampaignStatus === 1
        && EMAIL_SENT_STATUSES.indexOf(m.EmailCampaignStatus ?? -1) > -1;
};

// § THE E-MAIL TAB LOCK (invariant 24). `HasLeg` is part of the predicate WORD FOR WORD, and it
// is load-bearing: it must be the same predicate as the "שלח לכולם" gate below. In the gap
// `waActive ∧ ¬HasLeg ∧ EmailCampaignStatus ∈ {2,4}` — reachable, because withWhatsApp is also
// lit from the URL — the tab would be locked while the button stayed live, and `openSummary`
// would then get `gid = null` and send the campaign WITHOUT its synthetic group.
// 12-fix-wave 7.4(f) — locked by §1.9 (D1): every e-mail status other than 1, 6 and 7 included.
// On a "both" page `waMapping` is the unit's GetMapping, whose `EmailCampaignStatus` is the e-mail's
// status (4.8(c)); NULL (no e-mail, or an API/DB before 4.8) reads as editable.
export const selectEmailLocked = (s: WaRootState): boolean => {
    const m = wa(s).waMapping;
    return selectWaActive(s)
        && (m?.EmailCampaignStatus ?? 1) !== 1;
};

// "שלח לכולם" is disabled in EVERY state where the e-mail is already out — waiting AND locked
// (v1.10 m08). op8 requires `ISNULL(Status,1)=1`, so the main send would answer 404
// CAMPAIGN_NOT_FOUND, a code that is not in §4 and has no i18n key at all.
export const selectWaBlocksSendToAll = (s: WaRootState): boolean => selectEmailLocked(s);

// ── receipt state (contract §7 — the ONLY place the wording of stuck/inFlight is defined) ────
// FIRST MATCH WINS, and the order is mandatory (v1.15): stuck → queued → inFlight. Without it
// `inFlight`, defined as the complement, overlaps "queued" and both light on one state.
export type WaReceiptState = 'stuck' | 'queued' | 'inFlight' | 'sent' | 'none';

export const selectWaReceiptState = (s: WaRootState): WaReceiptState => {
    const m = wa(s).waMapping;
    if (!m || !m.HasLeg || !m.HasHeader) return 'none';
    const status = m.WaCampaignStatus;
    const ageMin = m.WaSendAgeMin;
    // `WaSendAgeMin === null` (no live header, or SendDate NULL) ⇒ NONE of the three receipts
    // is shown. That is already the rule in state (5), where every header counter is NULL.
    if (status === 3) return ageMin == null ? 'none' : 'stuck';
    if (status === 4) return 'sent';
    if (status !== 2) return 'none';
    if (ageMin == null) return 'none';
    // 🔴 `WaSendAgeMin >= 15` gates BOTH disjuncts (v1.14 Q3-1, MAJOR). `WhatsAppLog` — the only
    // link between a row and its header — is written at spUiApiWhatsappSendLogs_Stage:114, i.e.
    // AFTER the charge in step 13 and after most of the writer's work. Without the age gate the
    // `WaRows180 = 0` disjunct fires on a LIVE send and the screen says "stuck, do not retry"
    // at the exact moment the writer is charging and dispatching.
    const stuck = (m.WaRowsQueued ?? 0) === 0
        && ageMin >= 15
        && ((m.WaRows180 ?? 0) === 0 || (m.WaRows180AgeMin ?? 0) >= 15);
    if (stuck) return 'stuck';
    if ((m.WaRowsQueued ?? 0) > 0) return 'queued';
    return 'inFlight';
};

// Whether the retry BUTTON is offered, as opposed to whether the server would accept one.
// Two subtractions from `selectWaRetryEligible`, and both are about not charging twice:
//   · `stuck` — the contract's own wording is "אל תנסו שוב", so the button must not sit under it;
//   · `INTERNAL_AFTER_DISPATCH` — the messages MAY already have gone out (this is also what a
//     LOST RESPONSE on a retry is recorded as), and a second press would charge and re-send
//     whatever left. The report is the only source of truth there.
// Defined here rather than in each of the two render sites so the mapping tab and the receipt
// cannot disagree about it.
export const selectWaRetryOffered = (s: WaRootState): boolean =>
    selectWaRetryEligible(s)
    && selectWaReceiptState(s) !== 'stuck'
    && wa(s).waSendOutcome?.Reason !== 'INTERNAL_AFTER_DISPATCH'
    // 🔴 WITHOUT THIS THE NEW RECEIPT LINE ARRIVES WITH A BUTTON THAT CANNOT WORK. PUT
    // WhatsApp/Send answers 409 CHANNEL_NOT_ACTIVE for exactly this state
    // (DataSourcesSenderController.cs:3228-3236) and that token has no `error.*` text, so
    // waErrorText falls back to WA_INTERNAL: the press tells the operator to call support about an
    // internal error, over a channel she switched off herself. The way back is the channel switch.
    // Two clauses: the outcome answers on the receipt right after the send, the live array answers
    // on the mapping tab and after a refresh. `!== false`, not `=== true`, so "not read yet" (null)
    // leaves today's behaviour alone.
    && wa(s).waSendOutcome?.Reason !== 'CHANNEL_NOT_ACTIVE'
    && selectServerChannelActive(s, 'whatsapp') !== false;

// The channels this campaign will actually send on.
// 🔴 v1.23 REVERSED v1.20(ג). The previous body hard-coded "there is NO WhatsApp-only route in
// the MVP" and returned `[EMAIL]` or `[EMAIL, WHATSAPP]`; the owner reversed that decision on
// 2026-09-14 ("זה צריך להיות גנרי — לפעמים הם ירצו לשלוח רק וואטסאפ ממקור"), so the third
// combination is now reachable and this list is the single place that says which.
export const selectActiveChannels = (s: WaRootState): eSendChannel[] => {
    if (!selectWaActive(s)) return [eSendChannel.EMAIL];
    return selectWaOnly(s)
        ? [eSendChannel.WHATSAPP]
        : [eSendChannel.EMAIL, eSendChannel.WHATSAPP];
};

// 🔴 THE IDENTITY GATE OF THE SOURCE PICKER — AN **OR** OVER THE ACTIVE CHANNELS (§6, v1.23b).
//
// A source is selectable if it serves AT LEAST ONE live channel. Serving none is "view only".
//
// ⚠️ IT WAS AN `AND` FOR ONE ROUND, AND THE `AND` WAS THE CONTRACT'S OWN WORDING BEFORE v1.23b
// CORRECTED IT. Two things were wrong with it, and they are worth keeping written down because
// the shape is the intuitive one:
//  1. It blocked a CELL-ONLY source on a WhatsApp-only campaign — the exact source the feature
//     was built to send from. The owner's sentence: "וואטסאפ בלי אימייל זה כל הרעיון."
//  2. It contradicted "אזהרה ולא חסימה" three lines below itself in the same section: a rule
//     that blocks the card can never reach the banner that is supposed to warn about it.
//
// ⚠️ AND THE CONSEQUENCE IS ACCEPTED, NOT OVERLOOKED (§6 says so explicitly): with BOTH
// channels lit and a cell-only source, the e-mail leg goes out to ZERO recipients. That is not
// a silent outcome — it is precisely what `mapping.noEmailBannerBoth` states on the screen.
//
// The old shape it replaced was `canSend = !!it[descriptor.identityFlag]`: ONE flag, chosen by
// `selectedChannel`, which cannot express WhatsApp at all (`SmartSend.ts:32-34`,
// `enabled: false`, byte-identical). Both flags already ride on every `DataSources_GetMany`
// row, so nothing new is fetched.
//
// ⚠️ It takes the LIST ROW, not the leg. `waMapping.HasCellIdentity` is a different fact — the
// server resolving the LOCKED VERSION — and it is what drives the mapping tab's own banner and
// `waSaveState='blocked'`. Neither of those ever blocks a send (§6); this one decides which
// source cards can be PICKED. Two gates, two questions; do not fold them together.
// A PURE function of (row, flags) rather than a selector of the store, for two reasons that
// both bit elsewhere in this feature: a selector returning a fresh closure or object makes
// `useSelector` re-render on every dispatch, and a rule that cannot be called without a store
// cannot be unit-tested — which is how `shouldFetchPreview` ended up extracted too.
// 🔴 `ActiveChannels`, NOT A PRIVATE `{ emailOn, waOn }` PAIR — AND THIS IS THE WHOLE POINT
// OF THE FOLD, NOT TIDINESS. The old shape had two fields named after two channels, so an
// SMS-only campaign passed `{ emailOn: false, waOn: false }` and this returned `false` FOR
// EVERY SOURCE: every card blocked, the route unreachable, and no line of code to blame.
// **That is the same bug that already shipped once** — the paragraph above records it ("וואטסאפ
// בלי אימייל זה כל הרעיון") and v1.23b fixing it. A predicate that enumerates the live channels
// cannot be wrong about a channel it has never heard of.
export const sourceCanSend = (it: any, ch: ActiveChannels): boolean => !!it
    && activeChannelKeys(ch).some((k) => !!it[CHANNELS_BY_KEY[k].identityFlag]);

// Which identity a given source is MISSING for a channel that is switched on. 'none' ⇒ nothing
// is missing. A source that fails both is "view only", and neither half of that is more
// informative than the other.
//
// 🔴 SINCE v1.23b THIS IS MOSTLY A QUESTION ABOUT **SELECTABLE** SOURCES. Under the `OR` gate a
// source can be perfectly pickable and still leave one lit channel with nothing to send to —
// that combination IS the feature's normal state, and the banner is the only thing that says
// so. It still answers for blocked cards too (where it names the chip), and the two uses agree
// because they ask the same question.
// ⚠️ AND IT ANSWERS ABOUT AN **IDENTITY**, NOT ABOUT A CHANNEL — which is why `needs()` asks
// the descriptors rather than the channel keys. WhatsApp and SMS both ride on `HasCellIdentity`,
// so "a cell column is missing" must be true when EITHER is lit, and 'cell' must not become two
// answers the day the second one arrives.
export const missingIdentity = (
    it: any, ch: ActiveChannels,
): 'none' | 'email' | 'cell' | 'both' => {
    const needs = (flag: ChannelDescriptor['identityFlag']) =>
        activeChannelKeys(ch).some((k) => CHANNELS_BY_KEY[k].identityFlag === flag);
    // 🔴 NEITHER IDENTITY ⇒ 'both' IN EVERY CHANNEL MIX, AND THIS ARM MUST COME FIRST.
    // "לצפייה בלבד" is a fact about the SOURCE — a datalake with no address and no phone number
    // cannot reach anyone by any channel — so it does not depend on which channels are lit.
    // Without this arm, an e-mail-only campaign would re-label such a source "אין עמודת אימייל",
    // which is true and is the wrong half of the truth: it reads as "pick another channel",
    // and there is no channel this source can be sent on. That is a REGRESSION against the
    // behaviour this function replaced (`it.HasCellIdentity ? 'blockedNoEmail' : 'viewOnly'`),
    // on the path where the WhatsApp add-on is switched off entirely.
    if (!it?.HasEmailIdentity && !it?.HasCellIdentity) return 'both';
    const noEmail = needs('HasEmailIdentity') && !it?.HasEmailIdentity;
    const noCell = needs('HasCellIdentity') && !it?.HasCellIdentity;
    if (noEmail && noCell) return 'both';
    if (noEmail) return 'email';
    if (noCell) return 'cell';
    return 'none';
};

// Channel descriptors excluded from ChannelSelector. Two reasons now, and the second is a guard:
//
//  1. While the add-on tile is on screen, the WhatsApp card must not sit next to a live
//     "גם בוואטסאפ" tile — the original reason, and it still holds.
//
//  2. 🔴 AND WHEN THE ADD-ON IS *NOT* LIVE EITHER, BECAUSE THE SERVER STILL REFUSES CHANNEL 3.
//     `CHANNELS` now carries `enabled: true` for WhatsApp so that `setChannel(3)` is
//     representable at all (77 §2) — but `ChannelSelector.select` (:150) dispatches
//     `setChannel` for ANY card whose `enabled` is true, and `clearSmartSend` has ZERO dispatch
//     sites, so one click made `selectedChannel = 3` STICK FOR THE WHOLE SESSION. From there
//     every save hit `DataSourcesSenderController.cs:384`
//         if (req.Channel != eSendChannel.Email) return MapReturnCode(-99);
//     and the operator could not save an E-MAIL mapping again until they reloaded the page — in
//     an account that never asked for WhatsApp. Before `enabled` was flipped the same card was
//     rendered but inert, so this is new. That is the red line of 66 §4: the card does not go
//     on screen until the route behind it exists.
//
// ⚠️ REMOVE THIS EXCLUSION only together with the server accepting channel 3 on SetMapping —
//    i.e. when :384 routes by channel instead of refusing, AND OwnsChannelContainer
//    (DataSourcesSenderController.cs:186-195) stops falling through to 501 and uses
//    eAuthenticateOpsType.WhatsAppCampaign = 11, which 78d already wired in the DB.
//    Until both are true, this card is a button whose only destination is -99.
export const selectExcludedChannelCards = (_s: WaRootState): eSendChannel[] =>
    [eSendChannel.WHATSAPP];

// 🔴 THE SOURCE ROW THE CAMPAIGN IS ACTUALLY ON — and it is the ONLY place the identity flags
// can be read from. `smartSend.dataSource` is `SmartSendDataSourceInfo` (id, name, versions) and
// carries neither `HasEmailIdentity` nor `HasCellIdentity`; those live on the `GetMany` LIST
// row, which `SourcePicker` fetches into `dataSources.list`.
//
// Defined here rather than inline because three surfaces now ask the same question — the
// picker's banners, the removal dialog's "can this campaign switch to e-mail at all", and the
// card gate — and an answer computed three ways is an answer that will disagree with itself.
//
// ⚠️ `.find` returns the array's OWN element, not a new object, so `useSelector` on this is
// referentially stable while the list is. Do not "improve" it into a mapped/spread result.
export const selectSelectedSourceRow = (s: WaRootState): any => {
    const id = s?.smartSend?.dataSource?.DataSourceID ?? s?.smartSend?.dataSourceId ?? null;
    if (id == null) return null;
    const items: any[] = s?.dataSources?.list?.items ?? [];
    return items.find((it: any) => it.DataSourceID === id) ?? null;
};

// 🔴 STRICTLY "the server said yes". An UNKNOWN source — the list not loaded, or a screen that
// never loads it — answers FALSE, and that is the safe direction here: the one consumer is the
// removal dialog's "switch to e-mail" option, and offering it on a source with no e-mail column
// converts the campaign to a channel that reaches nobody (§6). Withholding an option asserts
// nothing; offering it wrongly sends a campaign into a dead end.
export const selectSourceHasEmailIdentity = (s: WaRootState): boolean =>
    !!selectSelectedSourceRow(s)?.HasEmailIdentity;

export const CHANNEL_DESCRIPTORS = CHANNELS;
